import * as React from 'react';
import { View } from 'react-native';
import Animated, { withTiming, interpolate, Extrapolation, useSharedValue, useAnimatedProps, useAnimatedStyle, Reanimated3DefaultSpringConfig, withSpring, } from 'react-native-reanimated';
import Svg from 'react-native-svg';
import Text from 'design-system/Text';
import { rem } from 'design-system/values';
import { AnimatedPath } from 'design-system/AnimatedComponents';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import createStyleSheets from 'utils/createStyleSheets';
import { MAX_MIN_INDICATOR_VIEW_HEIGHT } from '../constants';
const MinPriceIndicatorView = ({ minPoint, width, height, selectedInfoObj }) => {
    const didMount = React.useRef(false);
    const styles = useStyles(styleSet);
    const colors = useColors();
    const x = useSharedValue(minPoint.x);
    const y = useSharedValue(minPoint.y);
    React.useEffect(() => {
        if (didMount.current) {
            x.value = withTiming(minPoint.x);
            y.value = withTiming(minPoint.y);
        }
        else {
            didMount.current = true;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [width, minPoint]);
    const animatedLineProps = useAnimatedProps(() => {
        const props = {
            d: `M${x.value * width} 8 l0 8`,
        };
        return props;
    }, [width, x]);
    const leftViewAnimatedStyles = useAnimatedStyle(() => ({
        flex: interpolate(x.value, [0, 0.05, 0.25, 0.5, 0.75, 0.95, 1], [0, 0, 0.2, 0.5, 0.8, 1, 1], Extrapolation.CLAMP),
    }), [x]);
    const rightViewAnimatedStyles = useAnimatedStyle(() => ({
        flex: interpolate(x.value, [0, 0.05, 0.25, 0.5, 0.75, 0.95, 1], [1, 1, 0.8, 0.5, 0.2, 0, 0], Extrapolation.CLAMP),
    }), [x]);
    const translateYStyles = useAnimatedStyle(() => ({
        transform: [
            {
                translateY: y.value * height,
            },
        ],
        opacity: withSpring(!selectedInfoObj.value ? 1 : 0, Reanimated3DefaultSpringConfig),
    }), [height, selectedInfoObj, y]);
    return (<View pointerEvents="none" style={styles.container}>
      <Animated.View style={translateYStyles}>
        <Svg width={width} height={MAX_MIN_INDICATOR_VIEW_HEIGHT} viewBox={`0 0 ${width} ${MAX_MIN_INDICATOR_VIEW_HEIGHT}`}>
          <AnimatedPath strokeWidth={1} animatedProps={animatedLineProps} stroke={colors.elements.secondary}/>
        </Svg>
        <View style={styles.textContainer}>
          <Animated.View style={leftViewAnimatedStyles}/>
          <Text NumbersThin-12 Secondary>
            {minPoint.formattedPrice}
          </Text>
          <Animated.View style={rightViewAnimatedStyles}/>
        </View>
      </Animated.View>
    </View>);
};
const MinPriceIndicator = ({ minPoint, width, height, selectedInfoObj }) => {
    const didMount = React.useRef(false);
    const [privateMinPoint, setPrivateMinPoint] = React.useState(minPoint);
    React.useEffect(() => {
        if (didMount.current) {
            if (minPoint !== undefined) {
                setPrivateMinPoint(minPoint);
            }
        }
        else {
            didMount.current = true;
        }
    }, [minPoint]);
    if (!privateMinPoint) {
        return null;
    }
    return (<MinPriceIndicatorView width={width} height={height} minPoint={privateMinPoint} selectedInfoObj={selectedInfoObj}/>);
};
export default React.memo(MinPriceIndicator);
const styleSet = createStyleSheets(() => ({
    container: {
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1,
        top: MAX_MIN_INDICATOR_VIEW_HEIGHT,
        position: 'absolute',
    },
    textContainer: {
        top: rem(18),
        left: rem(2),
        right: rem(2),
        bottom: 0,
        position: 'absolute',
        flexDirection: 'row',
    },
}));
